import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import axios from "axios";
import { FileText, Shield, User, Upload, Search, Wallet, LogOut } from "lucide-react";

const Eduvault = () => {
  const [contract, setContract] = useState(null);
  const [usn, setUsn] = useState("");
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [account, setAccount] = useState(null);
  const [isOwner, setIsOwner] = useState(null);
  const [instituteAddress, setInstituteAddress] = useState("");
  const [studentRecords, setStudentRecords] = useState([]);
  const [name, setName] = useState("");
  const [instituteName, setInstituteName] = useState("");
  const [file, setFile] = useState(null);
  const [certificateHash, setCertificateHash] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);

  const contractAddress = "0xe5190613c5715c18306ad6022be52cce0dcf42e2";
  const pinataDomain = process.env.REACT_APP_PINATA_DOMAIN;

  const uploadToPinata = async (file) => {
    const url = `https://api.pinata.cloud/pinning/pinFileToIPFS`;
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(url, formData, {
        maxBodyLength: "Infinity",
        headers: {
          "Content-Type": `multipart/form-data`,
          pinata_api_key: process.env.REACT_APP_PINATA_API_KEY,
          pinata_secret_api_key: process.env.REACT_APP_PINATA_SECRET_API_KEY,
        },
      });

      console.log("Pinata response:", res.data);
      return res.data.IpfsHash;
    } catch (error) {
      console.error("Error uploading to Pinata:", error);
      throw error;
    }
  };

  const contractABI = [
    {
      inputs: [
        { internalType: "string", name: "_USN", type: "string" },
        { internalType: "string", name: "_name", type: "string" },
        { internalType: "string", name: "_instituteName", type: "string" },
        { internalType: "string", name: "_certificateHash", type: "string" },
      ],
      name: "addRecord",
      outputs: [],
      stateMutability: "nonpayable",
      type: "function",
    },
    {
      inputs: [{ internalType: "address", name: "_institutionID", type: "address" }],
      name: "authorizeInstitution",
      outputs: [],
      stateMutability: "nonpayable",
      type: "function",
    },
    {
      inputs: [],
      stateMutability: "nonpayable",
      type: "constructor",
    },
    {
      anonymous: false,
      inputs: [
        { indexed: true, internalType: "string", name: "USN", type: "string" },
        { indexed: true, internalType: "address", name: "institutionID", type: "address" },
        { indexed: false, internalType: "string", name: "certificateHash", type: "string" },
      ],
      name: "RecordAdded",
      type: "event",
    },
    {
      inputs: [{ internalType: "address", name: "_institutionID", type: "address" }],
      name: "revokeInstitution",
      outputs: [],
      stateMutability: "nonpayable",
      type: "function",
    },
    {
      inputs: [],
      name: "getOwner",
      outputs: [{ internalType: "address", name: "", type: "address" }],
      stateMutability: "view",
      type: "function",
    },
    {
      inputs: [{ internalType: "string", name: "_USN", type: "string" }],
      name: "getStudentRecord",
      outputs: [
        {
          components: [
            { internalType: "string", name: "USN", type: "string" },
            { internalType: "string", name: "name", type: "string" },
            { internalType: "address", name: "institutionID", type: "address" },
            { internalType: "string", name: "instituteName", type: "string" },
            { internalType: "string", name: "certificateHash", type: "string" },
            { internalType: "uint256", name: "timestamp", type: "uint256" },
          ],
          internalType: "struct EducationRecords.Record[]",
          name: "",
          type: "tuple[]",
        },
      ],
      stateMutability: "view",
      type: "function",
    },
  ];

  const connectWallet = async () => {
    setIsConnecting(true);
    try {
      if (!window.ethereum) {
        alert("MetaMask not found! Please install it.");
        return;
      }

      const sepoliaChainId = "0xaa36a7";
      try {
        await window.ethereum.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: sepoliaChainId }],
        });
      } catch (switchError) {
        if (switchError.code === 4902) {
          await window.ethereum.request({
            method: "wallet_addEthereumChain",
            params: [
              {
                chainId: sepoliaChainId,
                chainName: "Sepolia Test Network",
                nativeCurrency: {
                  name: "SepoliaETH",
                  symbol: "ETH",
                  decimals: 18,
                },
                rpcUrls: ["https://rpc.sepolia.org"],
                blockExplorerUrls: ["https://sepolia.etherscan.io"],
              },
            ],
          });
        } else {
          throw switchError;
        }
      }
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      await provider.send("eth_requestAccounts", []);
      const signer = provider.getSigner();

      setProvider(provider);
      setSigner(signer);

      const accountAddress = await signer.getAddress();
      setAccount(accountAddress);

      const contract = new ethers.Contract(contractAddress, contractABI, signer);
      setContract(contract);

      const ownerAddress = await contract.getOwner();
      setIsOwner(accountAddress.toLowerCase() === ownerAddress.toLowerCase());
    } catch (error) {
      console.error("Error connecting to wallet", error);
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectWallet = () => {
    setProvider(null);
    setSigner(null);
    setAccount(null);
    setContract(null);
    setIsOwner(null);
  };

  useEffect(() => {
    if (!account) {
      connectWallet();
    }
  }, []);

  const fetchStudentRecord = async () => {
    try {
      const records = await contract.getStudentRecord(usn);
      setStudentRecords(records);
    } catch (error) {
      console.log("Error fetching student record", error);
    }
  };

  const addRecord = async () => {
    try {
      const ipfsHash = await uploadToPinata(file);
      console.log("Got IPFS Hash:", ipfsHash);
      setCertificateHash(ipfsHash);
      const tx = await contract.addRecord(usn, name, instituteName, ipfsHash);
      await tx.wait();
      alert("Record added successfully!");
    } catch (error) {
      console.log("Error adding record", error);
    }
  };

  const authorizeInstitution = async () => {
    if (isOwner) {
      try {
        const tx = await contract.authorizeInstitution(instituteAddress);
        await tx.wait();
        alert("Institution authorized successfully!");
      } catch (error) {
        console.error("Error authorizing institution", error);
        alert("Failed to authorize institution.");
      }
    } else {
      alert("Only contract owner can authorize institutions.");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-black text-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <FileText className="w-8 h-8" />
              <h1 className="text-2xl font-bold tracking-tight">EduVault</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              {account && (
                <div className="hidden sm:flex items-center space-x-2 text-sm">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-gray-300">
                    {account.slice(0, 6)}...{account.slice(-4)}
                  </span>
                  {isOwner && (
                    <span className="bg-gray-800 px-2 py-1 rounded text-xs font-medium">
                      OWNER
                    </span>
                  )}
                </div>
              )}
              
              {account ? (
                <button
                  onClick={disconnectWallet}
                  className="flex items-center space-x-2 bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded-lg transition-colors duration-200"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Disconnect</span>
                </button>
              ) : (
                <button
                  onClick={connectWallet}
                  disabled={isConnecting}
                  className="flex items-center space-x-2 bg-white text-black hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors duration-200 disabled:opacity-50"
                >
                  <Wallet className="w-4 h-4" />
                  <span>{isConnecting ? "Connecting..." : "Connect Wallet"}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!account ? (
          <div className="text-center py-12">
            <Wallet className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Connect Your Wallet</h2>
            <p className="text-gray-600 mb-6">Please connect your MetaMask wallet to access EduVault</p>
            <button
              onClick={connectWallet}
              disabled={isConnecting}
              className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors duration-200 disabled:opacity-50"
            >
              {isConnecting ? "Connecting..." : "Connect Wallet"}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-8">
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-center space-x-3 mb-6">
                  <User className="w-5 h-5 text-gray-700" />
                  <h2 className="text-xl font-semibold text-gray-900">Add Student Record</h2>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Student USN
                    </label>
                    <input
                      type="text"
                      value={usn}
                      onChange={(e) => setUsn(e.target.value)}
                      placeholder="Enter student USN"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Student Name
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter student name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Institute Name
                    </label>
                    <input
                      type="text"
                      value={instituteName}
                      onChange={(e) => setInstituteName(e.target.value)}
                      placeholder="Enter institute name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Certificate Document
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        onChange={(e) => setFile(e.target.files[0])}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800"
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={addRecord}
                    disabled={!usn || !name || !instituteName || !file}
                    className="w-full bg-black text-white py-3 px-4 rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Add Record</span>
                  </button>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-center space-x-3 mb-6">
                  <Search className="w-5 h-5 text-gray-700" />
                  <h2 className="text-xl font-semibold text-gray-900">Fetch Student Record</h2>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Student USN
                    </label>
                    <input
                      type="text"
                      value={usn}
                      onChange={(e) => setUsn(e.target.value)}
                      placeholder="Enter student USN to search"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200"
                    />
                  </div>
                  
                  <button
                    onClick={fetchStudentRecord}
                    disabled={!usn}
                    className="w-full bg-black text-white py-3 px-4 rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <Search className="w-4 h-4" />
                    <span>Fetch Record</span>
                  </button>
                </div>
              </div>

              {isOwner && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <Shield className="w-5 h-5 text-gray-700" />
                    <h2 className="text-xl font-semibold text-gray-900">Authorize Institution</h2>
                    <span className="bg-black text-white px-2 py-1 rounded text-xs font-medium">
                      OWNER ONLY
                    </span>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Institution Address
                      </label>
                      <input
                        type="text"
                        value={instituteAddress}
                        onChange={(e) => setInstituteAddress(e.target.value)}
                        placeholder="Enter institution wallet address"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200"
                      />
                    </div>
                    
                    <button
                      onClick={authorizeInstitution}
                      disabled={!instituteAddress}
                      className="w-full bg-black text-white py-3 px-4 rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center space-x-2"
                    >
                      <Shield className="w-4 h-4" />
                      <span>Authorize Institution</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center space-x-3 mb-6">
                <FileText className="w-5 h-5 text-gray-700" />
                <h2 className="text-xl font-semibold text-gray-900">Student Records</h2>
                {studentRecords.length > 0 && (
                  <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium">
                    {studentRecords.length} {studentRecords.length === 1 ? 'Record' : 'Records'}
                  </span>
                )}
              </div>
              
              {studentRecords.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No records found</p>
                  <p className="text-sm text-gray-400 mt-1">Search for a student USN to view records</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {studentRecords.map((record, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors duration-200">
                      <div className="space-y-2">
                        <div className="flex justify-between items-start">
                          <h3 className="font-semibold text-gray-900">{record.name}</h3>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {new Date(record.timestamp * 1000).toLocaleDateString()}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Institute:</span> {record.instituteName}
                        </p>
                        
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">USN:</span> {record.USN}
                        </p>
                        
                        <div className="pt-2">
                          <a
                            href={`https://${pinataDomain}/ipfs/${record.certificateHash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 text-black hover:text-gray-600 font-medium text-sm transition-colors duration-200"
                          >
                            <FileText className="w-4 h-4" />
                            <span>View Certificate</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Eduvault;